import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import useCatalogue from "../../Services/Catalogue/useCatalogue";
import { GlobalContext } from "../../../Context/GlobalContext";

export const EXTRACT_COLUMNS = [
  "itemName",
  "itemDescription",
  "photo",
  "category",
  "price",
  "discountPrice",
  "variantName",
  "iconDish",
  "rackNumber",
  "expiryDate",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
  "car_order_price",
];

export const ESSENTIAL_COLUMNS = new Set([
  "itemName",
  "category",
  "price",
  "discountPrice",
  "variantName",
  "iconDish",
  "photo",
  "home_delivery_price",
  "takeaway_price",
  "dining_price",
  "room_order_price",
  "car_order_price",
]);

export const EDITABLE_COLUMNS = new Set(EXTRACT_COLUMNS);
export const UOM_SESSION_KEY = "extract_catalogue_selected_uom_codes";

export const DISH_OPTIONS = [
  { value: "Y", label: "Veg" },
  { value: "N", label: "Non-Veg" },
];

export function clean(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/^\uFEFF/, "")
    .replace(/\0/g, "")
    .trim();
}

export function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export function toNumberOrBlank(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;

  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const number = Number(cleaned);

  return Number.isFinite(number) ? number : fallback;
}

export function isNumericLike(key, value) {
  const k = String(key || "").toLowerCase();

  if (
    k.includes("price") ||
    k.includes("discount") ||
    k.includes("rack") ||
    k.includes("stock")
  ) {
    return true;
  }

  return typeof value === "number";
}

function getResponseRows(apiResponse) {
  if (Array.isArray(apiResponse)) return apiResponse;
  if (Array.isArray(apiResponse?.response)) return apiResponse.response;
  if (Array.isArray(apiResponse?.data)) return apiResponse.data;
  if (Array.isArray(apiResponse?.result)) return apiResponse.result;
  return [];
}

export function createEmptyExtractRow() {
  return {
    id: `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    itemName: "",
    itemDescription: "",
    photo: "",
    category: "",
    price: "",
    discountPrice: "",
    variantId: "",
    variantName: "",
    iconDish: "",
    rackNumber: "",
    expiryDate: "",
    home_delivery_price: "",
    takeaway_price: "",
    dining_price: "",
    room_order_price: "",
    car_order_price: "",
  };
}

export function normalizeExtractRows(apiResponse) {
  const sourceRows = getResponseRows(apiResponse);

  return sourceRows
    .map((row, index) => {
      const price = toNumberOrBlank(row?.price, "");
      const fallbackPrice = price === "" ? "" : price;

      return {
        id: `${index}-${clean(row?.itemName)}-${Date.now()}`,
        itemName: clean(row?.itemName),
        itemDescription: clean(row?.itemDescription),
        photo: clean(row?.photo),
        category: clean(row?.category),
        price,
        discountPrice: toNumberOrBlank(row?.discountPrice, 0),

        variantId: "",
        variantName: "",

        iconDish: ["Y", "N"].includes(clean(row?.iconDish).toUpperCase())
          ? clean(row?.iconDish).toUpperCase()
          : "N",

        rackNumber: toNumberOrBlank(row?.rackNumber, 0),
        expiryDate: clean(row?.expiryDate),

        home_delivery_price: toNumberOrBlank(
          row?.home_delivery_price,
          fallbackPrice,
        ),
        takeaway_price: toNumberOrBlank(row?.takeaway_price, fallbackPrice),
        dining_price: toNumberOrBlank(row?.dining_price, fallbackPrice),
        room_order_price: toNumberOrBlank(row?.room_order_price, fallbackPrice),
        car_order_price: toNumberOrBlank(row?.car_order_price, fallbackPrice),
      };
    })
    .filter((row) => row.itemName || row.category || row.price !== "");
}

function normalizeImportedHeader(value) {
  return clean(value);
}

function validateImportedHeaders(headers = []) {
  const cleanedHeaders = headers.map(normalizeImportedHeader);

  const missingColumns = EXTRACT_COLUMNS.filter(
    (column) => !cleanedHeaders.includes(column),
  );

  const extraColumns = cleanedHeaders.filter(
    (column) => column && !EXTRACT_COLUMNS.includes(column),
  );

  if (missingColumns.length || extraColumns.length) {
    return {
      valid: false,
      message: [
        missingColumns.length
          ? `Missing columns: ${missingColumns.join(", ")}`
          : "",
        extraColumns.length ? `Extra columns: ${extraColumns.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join(" | "),
    };
  }

  return { valid: true, message: "" };
}

function normalizeExcelRows(jsonRows = []) {
  return jsonRows
    .map((row, index) => {
      const mapped = createEmptyExtractRow();

      EXTRACT_COLUMNS.forEach((column) => {
        mapped[column] = clean(row?.[column]);
      });

      mapped.id = `excel-${index}-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;

      mapped.variantId = "";
      mapped.iconDish = clean(mapped.iconDish).toUpperCase();

      return mapped;
    })
    .filter((row) => EXTRACT_COLUMNS.some((column) => clean(row[column])));
}

function getMissingColumnsForRow(row, variantOptions) {
  const missing = [];

  EXTRACT_COLUMNS.forEach((column) => {
    if (!clean(row?.[column])) {
      missing.push(column);
    }
  });

  const selectedVariant = findVariantOption(row, variantOptions);

  if (!selectedVariant?.sizeId) {
    missing.push("variantId");
  }

  return missing;
}

export function downloadExtractExcel(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    toast.error("No extracted rows available to download");
    return;
  }

  const excelRows = rows.map((row) => {
    const mapped = {};

    EXTRACT_COLUMNS.forEach((column) => {
      mapped[column] = row?.[column] ?? "";
    });

    return mapped;
  });

  const worksheet = XLSX.utils.json_to_sheet(excelRows, {
    header: EXTRACT_COLUMNS,
    skipHeader: false,
  });

  worksheet["!cols"] = EXTRACT_COLUMNS.map((column) => {
    const maxLength = Math.max(
      column.length,
      ...excelRows.map((row) => String(row?.[column] ?? "").length),
    );

    return {
      wch: Math.min(Math.max(maxLength + 2, 14), 42),
    };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Extracted Catalogue");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const dateStr = new Date().toLocaleString("en-IN").replace(/[\/,: ]/g, "_");

  saveAs(blob, `Extracted_Catalogue_${dateStr}.xlsx`);
}

export function downloadSampleExtractExcel() {
  const sampleRow = {
    itemName: "Margherita Pizza",
    itemDescription: "Classic cheese pizza",
    photo: "margherita.jpg",
    category: "Pizza",
    price: "199",
    discountPrice: "179",
    variantName: "1 PCS",
    iconDish: "Y",
    rackNumber: "1",
    expiryDate: "2026/12/31",
    home_delivery_price: "199",
    takeaway_price: "179",
    dining_price: "199",
    room_order_price: "209",
    car_order_price: "219",
  };

  const worksheet = XLSX.utils.json_to_sheet([sampleRow], {
    header: EXTRACT_COLUMNS,
    skipHeader: false,
  });

  worksheet["!cols"] = EXTRACT_COLUMNS.map((column) => ({
    wch: Math.min(Math.max(column.length + 4, 16), 30),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Catalogue");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, "extract_catalogue_sample.xlsx");
}

function startsWithHttp(value) {
  return /^https?:\/\//i.test(clean(value));
}

export function joinUrl(baseUrl, value) {
  const base = clean(baseUrl).replace(/\/+$/, "");
  const file = clean(value).replace(/^\/+/, "");

  if (!file) return "";
  if (startsWithHttp(file) || file.startsWith("data:")) return file;
  if (!base) return file;

  return `${base}/${file}`;
}

export function findVariantOption(row, variantOptions) {
  const rowVariantId = clean(row?.variantId);
  const rowVariantName = clean(row?.variantName);

  return (
    variantOptions.find(
      (option) => rowVariantId && String(option.sizeId) === rowVariantId,
    ) ||
    variantOptions.find(
      (option) => rowVariantName && option.value === rowVariantName,
    ) ||
    null
  );
}

export function calculateDiscount(row) {
  const price = Number(row?.price || 0);
  const discountPrice = Number(row?.discountPrice || 0);

  if (!Number.isFinite(price) || !Number.isFinite(discountPrice)) return "0";

  if (price > discountPrice) {
    return String(Number((price - discountPrice).toFixed(2)));
  }

  return "0";
}

export function buildInsertItems({ rows, uploadedPath, variantOptions }) {
  return rows.map((row) => {
    const selectedVariant = findVariantOption(row, variantOptions);

    return {
      itemName: clean(row.itemName),
      itemId: "",
      status: "Y",
      itemDescription: clean(row.itemDescription),
      dish: "",
      photo: joinUrl(uploadedPath, row.photo),
      itemNameTrans: "",
      itemDescriptionTrans: "",
      category: clean(row.category),
      ratings: "0",
      price: clean(row.price),
      discount: calculateDiscount(row),
      discountPrice: clean(row.discountPrice),
      variantId: clean(selectedVariant?.sizeId || row.variantId),
      variantName: clean(selectedVariant?.value || row.variantName),
      sizeTrans: "",
      iconDish: clean(row.iconDish || "N"),
      position: "",
      catId: "",
      subCategory: "",
      categoryDescription: "",
      customizations: "[]",
      barcodeNumber: "",
      expiryDate: clean(row.expiryDate),
      rackNumber: clean(row.rackNumber),
      home_delivery_price: clean(row.home_delivery_price),
      takeaway_price: clean(row.takeaway_price),
      dining_price: clean(row.dining_price),
      room_order_price: clean(row.room_order_price),
      car_order_price: clean(row.car_order_price),
    };
  });
}


const formatName = (val) => {
  if (val === null || val === undefined) return "";

  return String(val)
    .replace(/^\uFEFF/, "")
    .replace(/\0/g, "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};
async function extractCatalogueImage({ fileName, image, api }) {
  if (!api) {
    throw new Error("Anlook API instance missing");
  }

  if (!(image instanceof File)) {
    throw new Error("Selected image is not a valid File");
  }

  const formData = new FormData();

  // Actual image binary with filename
  formData.append("image", image, image.name);

  const baseURL = String(api?.defaults?.baseURL || "")
    .trim()
    .replace(/\/+$/, "");

  const endpointPath = `/Chaafo/extract?fileName=${encodeURIComponent(
    fileName,
  )}`;

  const endpoint = baseURL ? `${baseURL}${endpointPath}` : endpointPath;

  const commonHeaders = api?.defaults?.headers?.common || {};

  const authorization =
    commonHeaders.Authorization || commonHeaders.authorization || "";

  console.log("EXTRACT URL:", endpoint);
  console.log("EXTRACT IMAGE:", {
    name: image.name,
    type: image.type,
    size: image.size,
  });

  for (const [key, value] of formData.entries()) {
    console.log("FORM DATA:", key, value);
  }

  const response = await fetch(endpoint, {
    method: "POST",

    // Content-Type manually mat dena.
    // Browser multipart boundary automatically set karega.
    headers: {
      Accept: "*/*",
      ...(authorization
        ? {
            Authorization: authorization,
          }
        : {}),
    },

    body: formData,
    credentials: "include",
  });

  const contentType = response.headers.get("content-type") || "";

  let data;

  if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    const text = await response.text();

    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  // Format itemName
if (Array.isArray(data)) {
  data = data.map((item) => ({
    ...item,
    itemName: formatName(item.itemName),
  }));
}

  console.log("EXTRACT STATUS:", response.status);
  console.log("EXTRACT RESPONSE:", data);

  if (!response.ok) {
    const errorMessage =
      data?.responseMessage ||
      data?.message ||
      data?.title ||
      `Extract API failed with status ${response.status}`;

    const error = new Error(errorMessage);

    error.response = {
      status: response.status,
      data,
    };

    throw error;
  }

  // Axios jaisa response return kar rahe hain,
  // kyunki existing code res.data read karta hai.
  return {
    data,
    status: response.status,
  };
}

async function uploadCatalogueZip({ folderName, zipFile, anlookApi }) {
  if (!anlookApi) {
    throw new Error("Anlook API instance missing");
  }

  const formData = new FormData();
  formData.append("folder_name", folderName);
  formData.append("file", zipFile);

  const response = await anlookApi.post(
    "/v1/uploads/catalog_items/uploadv2.php",
    formData,
    {
      headers: {
        Accept: "*/*",
      },
    },
  );

  const uploadStatus = response?.data?.status;

  const isSuccess =
    uploadStatus === true ||
    uploadStatus === 1 ||
    uploadStatus === "1" ||
    uploadStatus === "success";

  if (!isSuccess) {
    throw new Error(
      response?.data?.message ||
        response?.data?.responseMessage ||
        "ZIP upload failed",
    );
  }

  return response.data;
}

function getZipBaseName(file) {
  if (!file?.name) return "";
  return clean(file.name).replace(/\.zip$/i, "");
}

function normalizeNameForCompare(value) {
  return clean(value)
    .replace(/\.zip$/i, "")
    .replace(/\s+/g, "")
    .toLowerCase();
}

export function useExtractCatalogueController({ userID, onUploaded }) {
  const { api, anlookApi } = useContext(GlobalContext);

  const { getAdminUomMaster, getAdminSizeMaster, insertAdminCatalogItem } =
    useCatalogue();

  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const excelInputRef = useRef(null);

  const columnPanelRef = useRef(null);
  const uomPanelRef = useRef(null);
  const inputRef = useRef(null);
  const cellRefs = useRef({});

  const [fileName, setFileName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedZipFile, setSelectedZipFile] = useState(null);

  const [rows, setRows] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [changedCells, setChangedCells] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [workflowStep, setWorkflowStep] = useState("setup");
  const [saveStage, setSaveStage] = useState("");

  const [searchText, setSearchText] = useState("");
  const [columnSearchText, setColumnSearchText] = useState("");
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);

  const [uomOptions, setUomOptions] = useState([]);
  const [selectedUomCodes, setSelectedUomCodes] = useState(() => {
    try {
      const saved = sessionStorage.getItem(UOM_SESSION_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [sizeOptionsByUom, setSizeOptionsByUom] = useState({});
  const [uomLoading, setUomLoading] = useState(false);
  const [sizeLoading, setSizeLoading] = useState(false);
  const [showUomPanel, setShowUomPanel] = useState(false);

  const [visibleColumnMap, setVisibleColumnMap] = useState(() => {
    return EXTRACT_COLUMNS.reduce((acc, column) => {
      acc[column] = true;
      return acc;
    }, {});
  });

  const visibleColumns = useMemo(() => {
    return EXTRACT_COLUMNS.filter(
      (column) => visibleColumnMap[column] !== false,
    );
  }, [visibleColumnMap]);

  const selectedUomLabels = useMemo(() => {
    return selectedUomCodes.map((code) => {
      const found = uomOptions.find((item) => item.uom_code === code);
      return found ? `${found.uom_name} (${found.uom_code})` : code;
    });
  }, [selectedUomCodes, uomOptions]);

  const variantOptions = useMemo(() => {
    const merged = selectedUomCodes.flatMap((uomCode) => {
      const sizes = sizeOptionsByUom[uomCode] || [];

      return sizes.map((size) => ({
        uomCode,
        value: clean(size.size_name || size.size_code || ""),
        label: clean(size.size_name || size.size_code || ""),
        sizeCode: clean(size.size_code || ""),
        sizeId: clean(size.size_id || ""),
      }));
    });

    return Array.from(
      new Map(
        merged
          .filter((item) => item.value && item.sizeId)
          .map((item) => [`${item.uomCode}-${item.sizeId}`, item]),
      ).values(),
    );
  }, [selectedUomCodes, sizeOptionsByUom]);

  const defaultVariantOption = useMemo(() => {
    return variantOptions?.[0] || null;
  }, [variantOptions]);

  const defaultVariantValue = defaultVariantOption?.value || "";

  const filteredColumnOptions = useMemo(() => {
    const q = columnSearchText.trim().toLowerCase();
    if (!q) return EXTRACT_COLUMNS;

    return EXTRACT_COLUMNS.filter((column) => column.toLowerCase().includes(q));
  }, [columnSearchText]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    const mapped = rows.map((row, sourceRowIndex) => ({
      row,
      sourceRowIndex,
      rowKey: row?.id || `row-${sourceRowIndex}`,
    }));

    if (!q) return mapped;

    return mapped.filter(({ row }) =>
      EXTRACT_COLUMNS.some((column) =>
        displayValue(row?.[column]).toLowerCase().includes(q),
      ),
    );
  }, [rows, searchText]);

  const pendingCount = useMemo(
    () => Object.keys(changedCells).length,
    [changedCells],
  );

  const zipBaseName = useMemo(() => {
    return getZipBaseName(selectedZipFile);
  }, [selectedZipFile]);

  const zipNameMatches = useMemo(() => {
    if (!selectedZipFile || !fileName) return true;

    return (
      normalizeNameForCompare(zipBaseName) === normalizeNameForCompare(fileName)
    );
  }, [selectedZipFile, zipBaseName, fileName]);

  useEffect(() => {
    const loadUomMaster = async () => {
      try {
        setUomLoading(true);

        const result = await getAdminUomMaster({ sizeType: "0" });

        if (result.success) {
          setUomOptions(result.data || []);
        } else {
          setUomOptions([]);
          toast.error(result.message || "Failed to load UOM");
        }
      } catch (error) {
        console.error("UOM load error:", error);
        toast.error("Failed to load UOM");
      } finally {
        setUomLoading(false);
      }
    };

    loadUomMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(UOM_SESSION_KEY, JSON.stringify(selectedUomCodes));
    } catch (error) {
      console.error("UOM session save error:", error);
    }
  }, [selectedUomCodes]);

  useEffect(() => {
    const loadSizesForSelectedUoms = async () => {
      if (!selectedUomCodes.length) {
        setSizeOptionsByUom({});
        return;
      }

      try {
        setSizeLoading(true);

        const results = await Promise.all(
          selectedUomCodes.map(async (uomCode) => {
            const result = await getAdminSizeMaster({
              sizeType: uomCode,
            });

            return [uomCode, result.success ? result.data || [] : []];
          }),
        );

        setSizeOptionsByUom(Object.fromEntries(results));
      } catch (error) {
        console.error("Size load error:", error);
        toast.error("Failed to load size options");
      } finally {
        setSizeLoading(false);
      }
    };

    loadSizesForSelectedUoms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUomCodes]);

  useEffect(() => {
    if (!rows.length || !defaultVariantOption) return;

    const hasBlankVariant = rows.some(
      (row) => !row.variantName || !row.variantId,
    );

    if (!hasBlankVariant) return;

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        variantName: row.variantName || defaultVariantOption.value,
        variantId: row.variantId || defaultVariantOption.sizeId,
      })),
    );
  }, [defaultVariantOption, rows.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (uomPanelRef.current && !uomPanelRef.current.contains(event.target)) {
        setShowUomPanel(false);
      }

      if (
        columnPanelRef.current &&
        !columnPanelRef.current.contains(event.target)
      ) {
        setShowColumnPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editingCell]);

  const toggleUomSelection = useCallback((uomCode) => {
    setSelectedUomCodes((prev) => {
      const oldCodes = Array.isArray(prev) ? prev : [];

      if (oldCodes.includes(uomCode)) {
        return oldCodes.filter((code) => code !== uomCode);
      }

      return [...oldCodes, uomCode];
    });
  }, []);

  const clearSelectedUoms = useCallback(() => {
    setSelectedUomCodes([]);
    setSizeOptionsByUom({});
  }, []);

  const updateRowCell = useCallback(
    (sourceRowIndex, column, value, extra = {}) => {
      setRows((prev) =>
        prev.map((row, index) =>
          index === sourceRowIndex
            ? {
                ...row,
                [column]: value,
                ...extra,
              }
            : row,
        ),
      );

      setChangedCells((prev) => ({
        ...prev,
        [`${sourceRowIndex}-${column}`]: true,
      }));
    },
    [],
  );

  const applyDefaultVariantToBlankRows = useCallback(
    (incomingRows = rows) => {
      if (!defaultVariantOption) return incomingRows;

      return incomingRows.map((row) => ({
        ...row,
        variantName: row.variantName || defaultVariantOption.value,
        variantId: row.variantId || defaultVariantOption.sizeId,
      }));
    },
    [defaultVariantOption, rows],
  );

  const applyDefaultVariantToAllRows = useCallback(() => {
    if (!defaultVariantOption) {
      toast.error("No default variant available. Select UOM first.");
      return;
    }

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        variantName: defaultVariantOption.value,
        variantId: defaultVariantOption.sizeId,
      })),
    );

    setChangedCells((prev) => {
      const next = { ...prev };
      rows.forEach((_, index) => {
        next[`${index}-variantName`] = true;
      });
      return next;
    });

    toast.success(`Default variant applied: ${defaultVariantOption.value}`);
  }, [defaultVariantOption, rows]);

  const selectCell = useCallback(
    (rowIndex, colIndex) => {
      const column = visibleColumns[colIndex];
      const rowInfo = filteredRows[rowIndex];

      if (!column || !rowInfo) return;

      setSelectedCell({
        rowIndex,
        colIndex,
        column,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });
    },
    [visibleColumns, filteredRows],
  );

  const focusCell = useCallback((rowIndex, colIndex) => {
    const refKey = `${rowIndex}-${colIndex}`;
    const element = cellRefs.current[refKey];

    if (element) {
      element.focus();
      element.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, []);

  const startEdit = useCallback(
    (rowIndex, colIndex) => {
      const column = visibleColumns[colIndex];
      const rowInfo = filteredRows[rowIndex];

      if (!column || !rowInfo || !EDITABLE_COLUMNS.has(column)) return;

      setSelectedCell({
        rowIndex,
        colIndex,
        column,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });

      setEditingCell({
        rowIndex,
        colIndex,
        column,
        sourceRowIndex: rowInfo.sourceRowIndex,
        rowKey: rowInfo.rowKey,
      });

      if (column === "variantName") {
        const selectedVariant = findVariantOption(rowInfo.row, variantOptions);
        setDraftValue(selectedVariant?.sizeId || "");
      } else {
        setDraftValue(rowInfo.row?.[column] ?? "");
      }
    },
    [visibleColumns, filteredRows, variantOptions],
  );

  const stopEdit = useCallback(() => {
    setEditingCell(null);
    setDraftValue("");
  }, []);

  const saveEdit = useCallback(
    (moveDirection = null) => {
      if (!editingCell) return;

      const { rowIndex, colIndex, column, sourceRowIndex } = editingCell;

      if (column === "variantName") {
        const selectedVariant = variantOptions.find(
          (option) => String(option.sizeId) === String(draftValue),
        );

        updateRowCell(sourceRowIndex, column, selectedVariant?.value || "", {
          variantId: selectedVariant?.sizeId || "",
        });
      } else {
        updateRowCell(sourceRowIndex, column, draftValue);
      }

      setEditingCell(null);
      setDraftValue("");

      if (moveDirection === "down") {
        const nextRow = Math.min(rowIndex + 1, filteredRows.length - 1);

        setTimeout(() => {
          selectCell(nextRow, colIndex);
          focusCell(nextRow, colIndex);
        }, 0);
      } else if (moveDirection === "right") {
        const nextCol = Math.min(colIndex + 1, visibleColumns.length - 1);

        setTimeout(() => {
          selectCell(rowIndex, nextCol);
          focusCell(rowIndex, nextCol);
        }, 0);
      } else {
        setTimeout(() => {
          selectCell(rowIndex, colIndex);
          focusCell(rowIndex, colIndex);
        }, 0);
      }
    },
    [
      editingCell,
      draftValue,
      updateRowCell,
      filteredRows.length,
      visibleColumns.length,
      selectCell,
      focusCell,
      variantOptions,
    ],
  );

  const moveSelection = useCallback(
    (direction) => {
      if (!selectedCell) return;

      const { rowIndex, colIndex } = selectedCell;

      let nextRow = rowIndex;
      let nextCol = colIndex;

      if (direction === "up") nextRow = Math.max(0, rowIndex - 1);
      if (direction === "down") {
        nextRow = Math.min(filteredRows.length - 1, rowIndex + 1);
      }
      if (direction === "left") nextCol = Math.max(0, colIndex - 1);
      if (direction === "right") {
        nextCol = Math.min(visibleColumns.length - 1, colIndex + 1);
      }

      selectCell(nextRow, nextCol);

      setTimeout(() => {
        focusCell(nextRow, nextCol);
      }, 0);
    },
    [
      selectedCell,
      filteredRows.length,
      visibleColumns.length,
      selectCell,
      focusCell,
    ],
  );

  const handleImageSelect = (e) => {
    const image = e.target.files?.[0];

    if (!image) return;

    if (!(image instanceof File)) {
      toast.error("Invalid image file");
      e.target.value = "";
      return;
    }

    if (!image.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      e.target.value = "";
      return;
    }

    console.log("SELECTED IMAGE:", {
      name: image.name,
      type: image.type,
      size: image.size,
    });

    setSelectedImage(image);
    toast.success("Image selected");

    e.target.value = "";
  };

  const handleZipSelect = (e) => {
    const zip = e.target.files?.[0];
    if (!zip) return;

    const isZip =
      zip.name.toLowerCase().endsWith(".zip") ||
      zip.type === "application/zip" ||
      zip.type === "application/x-zip-compressed";

    if (!isZip) {
      toast.error("Please select a valid ZIP file");
      e.target.value = "";
      return;
    }

    setSelectedZipFile(zip);
    toast.success("ZIP selected");
    e.target.value = "";
  };

  const handleExtractDone = async () => {
    if (extracting) return;

    const finalFileName = clean(fileName);

    if (!finalFileName) {
      toast.error("Please enter folder name / file name");
      return;
    }

    if (!selectedImage) {
      toast.error("Please select image first");
      return;
    }

    try {
      setExtracting(true);
      setWorkflowStep("extracting");

      const res = await extractCatalogueImage({
        fileName: finalFileName,
        image: selectedImage,
        api,
      });

      const normalizedRows = applyDefaultVariantToBlankRows(
        normalizeExtractRows(res.data),
      );

      if (!normalizedRows.length) {
        toast.error("No catalogue rows found in extract response");
        setWorkflowStep(rows.length ? "review" : "setup");
        return;
      }

      setRows((prev) => [...prev, ...normalizedRows]);

      setSelectedCell(null);
      setEditingCell(null);
      setDraftValue("");
      setChangedCells({});
      setSelectedImage(null);
      setWorkflowStep("review");

      toast.success(
        rows.length
          ? `${normalizedRows.length} more rows added`
          : `${normalizedRows.length} catalogue rows extracted`,
      );
    } catch (error) {
      console.error("Extract direct API error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to extract catalogue",
      );

      setWorkflowStep(rows.length ? "review" : "setup");
    } finally {
      setExtracting(false);
    }
  };

  const handleAddManualRow = () => {
    const row = applyDefaultVariantToBlankRows([createEmptyExtractRow()])[0];

    setRows((prev) => [...prev, row]);
    setWorkflowStep("review");
    toast.success("Blank row added. Fill all columns before saving.");
  };

  const handleExcelFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileNameLower = file.name.toLowerCase();

    const allowed =
      fileNameLower.endsWith(".xlsx") ||
      fileNameLower.endsWith(".xls") ||
      fileNameLower.endsWith(".csv");

    if (!allowed) {
      toast.error("Please upload valid Excel or CSV file");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;

        const workbook = XLSX.read(data, {
          type: fileNameLower.endsWith(".csv") ? "string" : "array",
        });

        const sheetName = workbook.SheetNames?.[0];
        const worksheet = workbook.Sheets?.[sheetName];

        if (!worksheet) {
          toast.error("No sheet found in uploaded file");
          return;
        }

        const headerRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        const headers = headerRows?.[0] || [];
        const headerValidation = validateImportedHeaders(headers);

        if (!headerValidation.valid) {
          toast.error(
            `Excel columns must match table columns. ${headerValidation.message}`,
          );
          return;
        }

        const jsonRows = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
        });

        const importedRows = applyDefaultVariantToBlankRows(
          normalizeExcelRows(jsonRows),
        );

        if (!importedRows.length) {
          toast.error("No valid rows found in uploaded Excel");
          return;
        }

        setRows((prev) => [...prev, ...importedRows]);
        setWorkflowStep("review");

        toast.success(`${importedRows.length} rows imported from Excel`);
      } catch (error) {
        console.error("Excel import error:", error);
        toast.error("Excel file could not be imported");
      } finally {
        e.target.value = "";
      }
    };

    reader.onerror = () => {
      toast.error("Unable to read Excel file");
      e.target.value = "";
    };

    if (fileNameLower.endsWith(".csv")) {
      reader.readAsText(file, "UTF-8");
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const validateBeforeZipStep = () => {
    if (!userID) return "Please select client first";
    if (!clean(fileName)) return "Please enter folder name / file name";
    if (!rows.length) {
      return "Please extract, import, or add catalogue rows first";
    }

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const missingColumns = getMissingColumnsForRow(row, variantOptions);

      if (missingColumns.length) {
        return `Row ${index + 1} incomplete. Missing: ${missingColumns.join(
          ", ",
        )}`;
      }
    }

    return "";
  };

  const validateBeforeFinalSave = () => {
    const finalFolderName = clean(fileName);

    if (!userID) return "Please select client first";
    if (!finalFolderName) return "Please enter folder name / file name";
    if (!selectedZipFile) return "Please select ZIP file";
    if (!rows.length) return "Please extract catalogue rows first";

    if (!zipNameMatches) {
      return `ZIP name must match folder name. Folder: "${finalFolderName}", ZIP: "${zipBaseName}"`;
    }

    return validateBeforeZipStep();
  };

  const goToZipStep = () => {
    const validationMessage = validateBeforeZipStep();

    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    setWorkflowStep("zip");
  };

  const handleFinalSave = async () => {
    if (saving) return;

    const validationMessage = validateBeforeFinalSave();
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    const folderName = clean(fileName);

    try {
      setSaving(true);
      setWorkflowStep("saving");
      setSaveStage("uploading_zip");

 const zipResult = await uploadCatalogueZip({
   folderName,
   zipFile: selectedZipFile,
   anlookApi,
 });

      setSaveStage("saving_items");

      const items = buildInsertItems({
        rows,
        uploadedPath: zipResult.path,
        variantOptions,
      });

      const insertResult = await insertAdminCatalogItem({
        userid: userID,
        items,
      });

      if (!insertResult?.success) {
        toast.error(insertResult?.message || "Failed to save catalogue items");
        setWorkflowStep("zip");
        return;
      }

      toast.success(
        insertResult.message || "Catalogue items saved successfully",
      );

      onUploaded?.();

      setWorkflowStep("complete");
      setSaveStage("");
    } catch (error) {
      console.error("Final extract save error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save extracted catalogue",
      );

      setWorkflowStep("zip");
    } finally {
      setSaving(false);
      setSaveStage("");
    }
  };

  const handleClear = useCallback(() => {
    setFileName("");
    setSelectedImage(null);
    setSelectedZipFile(null);
    setRows([]);
    setSearchText("");
    setColumnSearchText("");
    setSelectedCell(null);
    setEditingCell(null);
    setDraftValue("");
    setChangedCells({});
    setWorkflowStep("setup");
    setSaveStage("");
  }, []);

  const handleStartNew = useCallback(() => {
    handleClear();
  }, [handleClear]);

  const handleBackToReview = useCallback(() => {
    setWorkflowStep("review");
  }, []);

  const toggleColumnVisibility = (column) => {
    setVisibleColumnMap((prev) => ({
      ...prev,
      [column]: prev[column] === false,
    }));

    setSelectedCell(null);
    setEditingCell(null);
  };

  const showAllColumns = () => {
    const next = {};

    EXTRACT_COLUMNS.forEach((column) => {
      next[column] = true;
    });

    setVisibleColumnMap(next);
    setSelectedCell(null);
    setEditingCell(null);
  };

  const showEssentialColumns = () => {
    const next = {};

    EXTRACT_COLUMNS.forEach((column) => {
      next[column] = ESSENTIAL_COLUMNS.has(column);
    });

    setVisibleColumnMap(next);
    setSelectedCell(null);
    setEditingCell(null);
  };

  const resetView = () => {
    showAllColumns();
    setSearchText("");
    setColumnSearchText("");
    setShowColumnPanel(false);
    setSelectedCell(null);
    setEditingCell(null);
    setDraftValue("");
  };

  const handleDownloadExcel = () => downloadExtractExcel(rows);
  const handleDownloadSampleExcel = () => downloadSampleExtractExcel();

  return {
    userID,

    fileInputRef,
    zipInputRef,
    excelInputRef,
    columnPanelRef,
    uomPanelRef,
    inputRef,
    cellRefs,

    fileName,
    setFileName,
    selectedImage,
    selectedZipFile,

    rows,
    editingCell,
    draftValue,
    setDraftValue,
    changedCells,
    extracting,
    saving,

    workflowStep,
    setWorkflowStep,
    saveStage,

    searchText,
    setSearchText,
    columnSearchText,
    setColumnSearchText,
    showColumnPanel,
    setShowColumnPanel,
    selectedCell,
    setSelectedCell,

    uomOptions,
    selectedUomCodes,
    sizeLoading,
    uomLoading,
    showUomPanel,
    setShowUomPanel,

    visibleColumnMap,
    visibleColumns,
    selectedUomLabels,
    variantOptions,
    defaultVariantOption,
    defaultVariantValue,
    filteredColumnOptions,
    filteredRows,
    pendingCount,

    zipBaseName,
    zipNameMatches,

    toggleUomSelection,
    clearSelectedUoms,
    updateRowCell,
    applyDefaultVariantToAllRows,

    selectCell,
    focusCell,
    startEdit,
    stopEdit,
    saveEdit,
    moveSelection,

    handleImageSelect,
    handleZipSelect,
    handleExcelFileSelect,
    handleExtractDone,
    handleAddManualRow,
    handleFinalSave,
    handleClear,
    handleDownloadExcel,
    handleDownloadSampleExcel,

    toggleColumnVisibility,
    showAllColumns,
    showEssentialColumns,
    resetView,

    goToZipStep,
    handleBackToReview,
    handleStartNew,
  };
}
